import { memo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import TourBookingPanel from '../TourBookingPanel';
import { tourAvailabilityRequest } from '../../../../store/slices/tour';
import { addToTrip, saveTripRequest, setSelfBookingAllowed } from '../../../../store/slices/trip';
import useScrollToElementById from '../../../../hooks/useScrollToElementById';
import getElementId from '../../../../helpers/getElementId';
import checkDoEnableTripConfigDecoupling from '../../../../helpers/selectors/checkDoEnableTripConfigDecoupling';
import { PageLinks } from '../../../../constants';

const TourBookingColumn = ({
  itinerary,
  tripId,
  tripRoomSearchInput,
  doShowPrice,
  onModalClose,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { pageName } = router.query;
  const isAddToBookedTrip = pageName === PageLinks.AddTour.pageName;
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
  const details = useSelector((state) => state.tour.details[itinerary.productId]);
  const doEnableTripConfigDecoupling = useSelector(checkDoEnableTripConfigDecoupling);
  const scrollToElement = useScrollToElementById();

  useEffect(() => {
    if (itinerary.productId && (!details || !details.availability)) {
      dispatch(tourAvailabilityRequest({ productId: itinerary.productId, isCalendar: true }));
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [itinerary.productId, details?.availability]);

  const buildItinerary = ({
    day,
    slot,
    quantities,
    guests,
    price,
    amountDueAtBooking,
    currency,
    pricingModelKind,
  }) =>
    JSON.parse(
      JSON.stringify({
        ...itinerary,
        selectedSlot: {
          productCode: slot.productCode,
          date: day.date,
          time: slot.time,
          slotDescription: slot.description,
          pricingModelKind,
          rateQuantities: quantities,
          guests,
          price,
          amountDueAtBooking,
          currencyCode: currency,
        },
        cost: { ...(itinerary.cost || {}), price, currencyCode: currency },
        id: +_.uniqueId(),
      }),
    );

  const handleScroll = useCallback(
    (event) => {
      if (!event) return;
      if (doEnableTripConfigDecoupling) {
        const containerWrapper = document.getElementById('main-section-wrapper');
        containerWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      const elementId = getElementId(event, '.parentDiv');
      if (elementId) {
        scrollToElement(elementId, 'banner');
      } else {
        scrollToElement(tripId, 'banner');
      }
    },
    [doEnableTripConfigDecoupling, scrollToElement, tripId],
  );

  const handleAddToTrip = (selection, event) => {
    const tourItinerary = buildItinerary(selection);

    if (isBrochurePage) {
      dispatch(setSelfBookingAllowed());
      dispatch(
        saveTripRequest({
          tripId,
          tripRoomSearchInput,
          trip: {
            tripId,
            tourItineraries: [tourItinerary],
          },
        }),
      );
      return;
    }

    if (isAddToBookedTrip) {
      dispatch(
        saveTripRequest({
          tripId,
          tripRoomSearchInput,
          ignoreUpdateTripOnSuccess: true,
          trip: {
            tripId,
            tourItineraries: [tourItinerary],
            doAddItineraryInBookedTrip: true,
          },
        }),
      );
      return;
    }

    dispatch(addToTrip({ tripId, trip: { tourItineraries: [tourItinerary] } }));

    if (onModalClose) onModalClose();
    handleScroll(event);
  };

  return (
    <TourBookingPanel
      itinerary={itinerary}
      details={details}
      onAddToTrip={handleAddToTrip}
      isAddDisabled={!tripId}
      doShowPrice={doShowPrice}
    />
  );
};

TourBookingColumn.propTypes = {
  itinerary: PropTypes.shape().isRequired,
  tripId: PropTypes.string,
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
  doShowPrice: PropTypes.bool,
  onModalClose: PropTypes.func,
};

TourBookingColumn.defaultProps = {
  tripId: '',
  tripRoomSearchInput: undefined,
  doShowPrice: true,
  onModalClose: undefined,
};

export default memo(TourBookingColumn);
